import React from "react";
import { Badge } from "@/components/ui/badge";
import { EntityLink } from "@/components/shared/EntityLink";
import { cn } from "@/lib/utils";
import {
  FileText,
  Film,
  Droplet,
  Layers,
  Wrench,
  Package,
} from "lucide-react";

export type MaterialCategoryType =
  | "PAPER"
  | "FILM"
  | "INK"
  | "GLUE"
  | "CHEMICAL"
  | "TOOLING"
  | "OTHER";

export interface MaterialCategoryConfig {
  key: MaterialCategoryType;
  label: string;
  badgeClass: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const MATERIAL_CATEGORY_CONFIGS: Record<
  MaterialCategoryType,
  MaterialCategoryConfig
> = {
  PAPER: {
    key: "PAPER",
    label: "Giấy in",
    badgeClass: "bg-purple-50 text-purple-700 border-purple-200",
    icon: FileText,
  },
  FILM: {
    key: "FILM",
    label: "Màng",
    badgeClass: "bg-sky-50 text-sky-700 border-sky-200",
    icon: Film,
  },
  INK: {
    key: "INK",
    label: "Mực in",
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    icon: Droplet,
  },
  GLUE: {
    key: "GLUE",
    label: "Keo",
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: Layers,
  },
  CHEMICAL: {
    key: "CHEMICAL",
    label: "Hóa chất",
    badgeClass: "bg-rose-50 text-rose-700 border-rose-200",
    icon: Droplet,
  },
  TOOLING: {
    key: "TOOLING",
    label: "Khuôn/Kẽm",
    badgeClass: "bg-indigo-50 text-indigo-700 border-indigo-200",
    icon: Wrench,
  },
  OTHER: {
    key: "OTHER",
    label: "Vật tư khác",
    badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
    icon: Package,
  },
};

/**
 * Hàm suy luận phân loại vật tư thông minh nếu bản ghi cũ thiếu category trong DB
 */
export function deduceMaterialCategory(
  category?: string | null,
  typeCode?: string | null,
  materialCode?: string | null,
  materialName?: string | null,
): MaterialCategoryConfig {
  const catUpper = category?.toUpperCase();
  if (catUpper && catUpper in MATERIAL_CATEGORY_CONFIGS) {
    return MATERIAL_CATEGORY_CONFIGS[catUpper as MaterialCategoryType];
  }

  const text = `${typeCode || ""} ${materialCode || ""} ${materialName || ""}`.toLowerCase();

  if (
    text.includes("giấy") ||
    text.includes("duplex") ||
    text.includes("ivory") ||
    text.includes("couche") ||
    text.includes("bristol") ||
    text.includes("kraft") ||
    text.includes("decal") ||
    text.includes("paper") ||
    text.includes("sheet") ||
    text.includes("gsm")
  ) {
    return MATERIAL_CATEGORY_CONFIGS.PAPER;
  }

  if (
    text.includes("màng") ||
    text.includes("film") ||
    text.includes("bopp") ||
    text.includes("thermal") ||
    text.includes("pe") ||
    text.includes("ngọc") ||
    text.includes("al115") ||
    text.includes("pet")
  ) {
    return MATERIAL_CATEGORY_CONFIGS.FILM;
  }

  if (
    text.includes("mực") ||
    text.includes("ink") ||
    text.includes("cyan") ||
    text.includes("magenta") ||
    text.includes("yellow") ||
    text.includes("black") ||
    text.includes("pantone")
  ) {
    return MATERIAL_CATEGORY_CONFIGS.INK;
  }

  if (
    text.includes("keo") ||
    text.includes("glue") ||
    text.includes("hotmelt") ||
    text.includes("pur")
  ) {
    return MATERIAL_CATEGORY_CONFIGS.GLUE;
  }

  if (
    text.includes("khuôn") ||
    text.includes("kẽm") ||
    text.includes("ctp") ||
    text.includes("plate") ||
    text.includes("die")
  ) {
    return MATERIAL_CATEGORY_CONFIGS.TOOLING;
  }

  if (text.includes("hóa chất") || text.includes("cồn") || text.includes("dung môi")) {
    return MATERIAL_CATEGORY_CONFIGS.CHEMICAL;
  }

  return MATERIAL_CATEGORY_CONFIGS.OTHER;
}

export interface MaterialIdentityCellProps {
  materialId?: string | null;
  materialCode: string;
  materialName: string;
  category?: string | null;
  typeCode?: string | null;
  typeLabel?: string | null;
  suppliedSize?: string | null;
  unit?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  showIcon?: boolean;
}

/**
 * Component Hiển thị Thông tin Nhận diện Vật tư Chuẩn 2 Tầng (Canonical 2-Tier Material Cell)
 * - Tầng 1: [Badge Phân Loại] + Mã Vật Tư (font-mono, đậm, màu indigo, semantic link)
 * - Tầng 2: Tên Vật Tư Đầy Đủ + Khổ / Quy Cách Cung Ứng
 */
export const MaterialIdentityCell: React.FC<MaterialIdentityCellProps> = ({
  materialId,
  materialCode,
  materialName,
  category,
  typeCode,
  typeLabel,
  suppliedSize,
  unit,
  className,
  onClick,
  showIcon = true,
}) => {
  const catConfig = deduceMaterialCategory(category, typeCode, materialCode, materialName);
  const IconComponent = catConfig.icon;

  const displayCode = materialCode || "VT-CHUA-MA";
  const displayName = materialName || "Vật tư chưa đặt tên";

  return (
    <div
      className={cn("flex flex-col gap-0.5 min-w-0 text-left py-0.5", className)}
      onClick={onClick}
    >
      {/* Tầng 1: Badge Phân Loại + Mã Vật Tư font-mono đậm */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge
          variant="outline"
          className={cn(
            "text-[10px] px-1.5 py-0 font-bold border flex items-center gap-1 shrink-0 shadow-none",
            catConfig.badgeClass,
          )}
          title={`Phân loại: ${catConfig.label}${typeLabel ? ` (${typeLabel})` : ""}`}
        >
          {showIcon && <IconComponent className="w-2.5 h-2.5 shrink-0" />}
          <span>{catConfig.label}</span>
        </Badge>

        {materialId ? (
          <EntityLink
            entityType="material"
            id={materialId}
            code={displayCode}
            showIcon={false}
            className="font-mono text-xs font-bold text-indigo-700 tracking-tight hover:text-indigo-900 transition-colors select-all"
            title={`Mã vật tư: ${displayCode}`}
          />
        ) : (
          <span
            className="font-mono text-xs font-bold text-indigo-700 tracking-tight hover:text-indigo-900 transition-colors select-all"
            title={`Mã vật tư: ${displayCode}`}
          >
            {displayCode}
          </span>
        )}

        {typeCode && typeCode !== displayCode && (
          <span className="text-[10px] text-slate-500 font-mono hidden sm:inline" title={typeLabel || typeCode}>
            ({typeCode})
          </span>
        )}
      </div>

      {/* Tầng 2: Tên Vật Tư Đầy Đủ + Quy Cách / Khổ Cung Ứng */}
      <div className="text-xs text-slate-900 font-medium leading-snug line-clamp-2">
        <span>{displayName}</span>
        {suppliedSize && (
          <span className="text-[11px] text-slate-500 font-normal ml-1">
            • <span className="font-medium text-slate-600">Khổ:</span> {suppliedSize}
          </span>
        )}
        {unit && (
          <span className="text-[11px] text-slate-400 font-normal ml-1">
            ({unit})
          </span>
        )}
      </div>
    </div>
  );
};
